import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { useTranslation } from 'react-i18next';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { appendQuery } from '@utils/helpers/build-urls.ts';

export interface UsageResult {
  language: string;
  units_used: number;
  usage_period_end: string;
  usage_period_start: string;
  cost: number;
  token_name: string;
}

interface CustomerUsageResponse {
  count: number;
  result: UsageResult[];
  total_results: number;
}

export interface UsageOptions {
  view?: 'year' | 'month' | 'week' | 'day' | 'invoice';
  split_by?: 'period' | 'language' | 'token' | 'subscription' | '';
}

export const useGetCustomerUsage = (customerId: string, opts: UsageOptions) => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const view = opts.view ?? 'invoice';
  const split_by = opts.split_by ?? '';

  const validViews = new Set(['year', 'month', 'week', 'day', 'invoice']);
  const validSplit = new Set([
    'period',
    'language',
    'token',
    'subscription',
    ''
  ]);

  const fetchCustomerUsage = async (): Promise<CustomerUsageResponse> => {
    if (!customerId) throw new Error('customerId is required');
    if (!validViews.has(view)) throw new Error(t('invalid-view-option'));
    if (!validSplit.has(split_by))
      throw new Error(t('invalid-split-by-option'));

    const { customerUsageByCustomerId } = GET_URL_NEW();
    const base = customerUsageByCustomerId(customerId, { offset, limit });
    const url = appendQuery(base, { view, split_by });

    const { data } = await Axios.get<CustomerUsageResponse>(url);
    return data;
  };

  return useQuery<CustomerUsageResponse, AxiosError<BaseError>>(
    ['customer-usage', customerId, offset, limit, view, split_by],
    fetchCustomerUsage,
    { keepPreviousData: true, enabled: !!customerId }
  );
};
